import Table from "./table";
import * as domUtils from "./utils/dom";
import { IconTable } from "@codexteam/icons";
import BtnTooltip from "../../tooltips/btnTooltip";
import {
  GridIcon,
  FirstRowIcon,
  FirstColumnIcon,
  DefaultTableBgIcon,
  AddColRight,
  AddColLeft,
  DeleteCol,
  AddRowAbove,
  AddRowBelow,
  DeleteRow,
  AltRowPrimary,
  AltColPrimary,
  AltRowSecondary,
  AltColSecondary,
  ResetIcon,
  VerticalTop,
  VerticalCenter,
  VerticalBottom,
  HorizontalLeft,
  HorizontalCenter,
  HorizontalRight,
  EditIcon,
} from "../../../editorjs/icons/icons";
import "@simonwep/pickr/dist/themes/monolith.min.css";

/**
 * @typedef {object} TableConfig - configuration that the user can set for the table
 * @property {number} rows - number of rows in the table
 * @property {number} cols - number of columns in the table
 */
/**
 * @typedef {object} Tune - setting for the table
 * @property {string} name - tune name
 * @property {HTMLElement} icon - icon for the tune
 * @property {boolean} isActive - default state of the tune
 * @property {void} setTune - set tune state to the table data
 */
/**
 * @typedef {object} TableData - object with the data transferred to form a table
 * @property {boolean} withHeading - setting to use cells of the first row as headings
 * @property {string[][]} content - two-dimensional array which contains table content
 */

/**
 * Table block for Editor.js
 */
export default class TableBlock {
  /**
   * Notify core that read-only mode is supported
   *
   * @returns {boolean}
   */
  static get isReadOnlySupported() {
    return true;
  }

  /**
   * Render plugin`s main Element and fill it with saved data
   *
   * @param {TableData} data — previously saved data
   * @param {TableConfig} config - user config for Tool
   * @param {object} api - Editor.js API
   * @param {boolean} readOnly - read-only mode flag
   */
  constructor({ data, config, api, readOnly, options }) {
    this.api = api;
    this.readOnly = readOnly;
    this.config = config;
    const defaultContent = config.defaultContent ?? null;
    this.data = {
      withHeadings:
        data && data.withHeadings
          ? data.withHeadings
          : defaultContent?.withHeadings ?? false,
      content:
        data && data.content ? data.content : defaultContent?.content ?? [],
      styled: {
        tableStyle: data.styled?.tableStyle || 0,
        customFirstRowHeight: data.styled?.customFirstRowHeight || false,
        firstRowHeight: data.styled?.firstRowHeight || "",
        customFirstColWidth: data.styled?.customFirstColWidth || false,
        firstColWidth: data.styled?.firstColWidth || "",
        gridType: data.styled?.gridType || 0,
        gridColor: data.styled?.gridColor || "rgba(0, 0, 0, 1)",
        fontColor: data.styled?.fontColor || "",
        fontSize: data.styled?.fontSize || "",
        fontWeight: data.styled?.fontWeight || 1,
        backgroundColor: data.styled?.backgroundColor || "",
        secondaryBGColor: data.styled?.secondaryBGColor || "",
        firstRowFontSize: data.styled?.firstRowFontSize || "",
        firstRowFontWeight: data.styled?.firstRowFontWeight || 1,
        firstRowFontColor: data.styled?.firstRowFontColor || "",
        firstRowBGColor: data.styled?.firstRowBGColor || "",
        firstColFontSize: data.styled?.firstColFontSize || "",
        firstColFontWeight: data.styled?.firstColFontWeight || 1,
        firstColFontColor: data.styled?.firstColFontColor || "",
        firstColBGColor: data.styled?.firstColBGColor || "",
        horizontalAlign: data.styled?.horizontalAlign || "start",
        verticalAlign: data.styled?.verticalAlign || "start",
        firstRowHorizontalAlign: data.styled?.horizontalAlign || "start",
        firstRowVerticalAlign: data.styled?.verticalAlign || "start",
        firstColHorizontalAlign: data.styled?.horizontalAlign || "start",
        firstColVerticalAlign: data.styled?.verticalAlign || "start",
      },
    };
    this.table = null;
    this.csvFileInput = null;

    this._CSS = {
      baseClass: this.api.styles.block,
      loading: this.api.styles.loader,
      settingsButton: this.api.styles.settingsButton,
      settingsButtonActive: this.api.styles.settingsButtonActive,

      /**
       * Tool's classes
       */
      tableStylesContainer: "cdx-table-styles-container",
      stylesGroup: "cdx-styles-group",
      groupOptions: "cdx-group-options",

      tableStylesGroup: "cdx-table-styles-group",
      tableStylesGroupLabel: "cdx-table-styles-group-label",
      tableStyleTypeOptions: "cdx-table-style-type-options",
      tableStyleCellsOptions: "cdx-table-styles-cells-options",

      gridStylesGroup: "cdx-grid-styles-group",
      gridStylesGroupOptions: "cdx-grid-styles-group-options",

      gridTypeLabel: "cdx-table-grid-label",
      gridTypeInput: "cdx-table-grid-input",
      gridColorInput: "cdx-table-grid-color-input",
      backgroundColorInput: "cdx-table-bg-input",
      backgroundColorInputSecondary: "cdx-table-bg-input-secondary",
      secondaryBGColorInput: "cdx-table-secondary-bg-input",
      firstRowBGInput: "cdx-table-first-row-bg-input",
      firstColBGInput: "cdx-table-first-row-bg-input",

      fontStyleGroup: "cdx-font-styles-group",
      fontStyleGroupLabel: "cdx-font-styles-group-label",
      fontStyleOptions: "cdx-font-styles-color-options",
      fontColorLabel: "cdx-table-font-color-label",
      fontColorInput: "cdx-table-font-color-input",
      firstRowFontColorLabel: "cdx-table-first-row-font-color-label",
      firstRowFontColorInput: "cdx-table-first-row-font-color-input",
      firstColFontColorLabel: "cdx-table-first-col-font-color-label",
      firstColFontColorInput: "cdx-table-first-col-font-color-input",

      columnControlsGroup: "cdx-column-controls-group",
      columnControlsGroupLabel: "cdx-column-controls-group-label",
      columnControlsOptions: "cdx-column-controls-options",
      columnAddRight: "cdx-column-add-right",
      columnAddLeft: "cdx-column-add-left",
      columnDelete: "cdx-column-delete",

      rowControlsGroup: "cdx-row-controls-group",
      rowControlsGroupLabel: "cdx-row-controls-group-label",
      rowControlsOptions: "cdx-row-controls-options",
      rowAddAbove: "cdx-row-add-above",
      rowAddBelow: "cdx-row-add-below",
      rowDelete: "cdx-row-delete",

      resetStylesGroup: "cdx-reset-styles-group",
      resetStylesButton: "cdx-reset-styles-button",
      confirmResetModalMain: "cdx-reset-styles-modal-main",
      confirmResetModalBackdrop: "cdx-confirm-reset-modal-backdrop",
      confirmResetModal: "cdx-confirm-reset-modal",
      confirmModalOptions: "cdx-confirm-options",
      confirmResetBtn: "cdx-confirm-reset-btn",
      cancelResetBtn: "cdx-cancel-reset-btn",

      horizontalAlignOptions: "cdx-horizontal-align-options",
      verticalAlignOptions: "cdx-vertical-align-options",
      pickerButtonGroup: "cdx-picker-button-group",

      controlsOptions: "cdx-controls-options",
      tableStyleLabel: "cdx-table-style-label",
      tableStyleInput: "cdx-table-style-input",
      editTableStyleBtn: "cdx-edit-table-style-btn",
      close: "cdx-shape-settings-close",
      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      save: "save",
      clear: "clear",
      cancel: "cancel",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      tuneButton: "editor-block-tune-button",
      editorFont: "editor-font",
      editorBlockToolbar: "editor-block-toolbar",
      editorBlockModalBackdrop: "editor-block-modal-backdrop",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    };
  }

  /**
   * Get Tool toolbox settings
   * icon - Tool icon's SVG
   * title - title to show in toolbox
   *
   * @returns {{icon: string, title: string}}
   */
  static get toolbox() {
    return {
      icon: IconTable,
      title: "Table",
    };
  }

  /**
   * Plugins styles
   *
   * @returns {{settingsWrapper: string}}
   */
  static get CSS() {
    return {
      settingsWrapper: "tc-settings",
      tunesWrapper: "tc-tunes",
    };
  }

  /**
   * CSV file handler
   */
  csvFileHandler(event) {
    event.preventDefault();

    let file = event.target.files[0];
    const that = this;

    if (file) {
      let reader = new FileReader();
      reader.readAsText(file, "UTF-8");
      reader.onload = function (e) {
        const csvFileContent = e.target.result;
        const delimiter = ",";

        const headers = csvFileContent
          .slice(0, csvFileContent.indexOf("\n"))
          .split(delimiter);
        const rows = csvFileContent
          .slice(csvFileContent.indexOf("\n") + 1)
          .split("\n");

        const arr = rows.map(function (row) {
          const values = row.split(delimiter);
          const el = headers.reduce(function (object, header, index) {
            object[header] = values[index];
            return object;
          }, {});
          return el;
        });

        that.data.withHeadings = true;

        const tableContent = [];
        tableContent.push(Object.keys(arr[0]));

        for (let i = 0; i < arr.length; i++) {
          tableContent.push(Object.values(arr[i]));
        }

        that.data.content = tableContent;

        const tempData = {};
        tempData.content = tableContent;
        tempData.withHeading = true;

        const oldTable = that.container.querySelectorAll(".tc-wrap");
        oldTable[0].remove();

        that.table = new Table(that.readOnly, that.api, tempData, that.config);
        that.container.appendChild(that.table.getWrapper());
        that.table.setHeadingsSetting(that.data.withHeadings);

        that.csvFileInput = that.container.querySelector(".csv-file-input");
        that.csvFileInput.addEventListener("change", (event) => {
          that.csvFileHandler(event);
        });
        if (that.config.draggable) {
          setTimeout(() => {
            that.applyStyles();
          }, 10);
        }
      };
      reader.onerror = function (e) {
        console.log("ERROR READING CSV FILE");
      };
    }
  }

  parseRGBA(rgbaString) {
    const values = rgbaString.match(
      /rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)/i
    );
    if (!values || values.length < 5) {
      return null;
    }

    return [
      Math.round(parseFloat(values[1])),
      Math.round(parseFloat(values[2])),
      Math.round(parseFloat(values[3])),
      parseFloat(values[4]),
    ];
  }

  createPopup = ({
    element,
    dataName,
    pickerName,
    fontSizeObj = null,
    fontWeightSelect = null,
    slider = null,
    alignComponent = null,
  }) => {
    const title = domUtils.make("label", ["picker-title", "popup-title"]);
    title.innerText = pickerName;

    let fontSizeElement = fontSizeObj?.element || null;
    let fontSizeElementContainer = null;
    if (fontSizeObj && fontSizeObj !== null && fontSizeObj?.element) {
      fontSizeElementContainer = domUtils.make("div", ["picker-component"]);
      const fontSizeControls = domUtils.make("div", ["picker-sub-component"]);
      const fontSizeTitle = domUtils.make("label", ["picker-title"]);
      const resetFontBtn = domUtils.make("button", ["picker-button"]);
      resetFontBtn.innerHTML = ResetIcon;
      fontSizeTitle.innerText = "Font size";

      BtnTooltip(resetFontBtn, "Reset to default size");

      resetFontBtn.addEventListener("click", () => {
        this.data.styled[fontSizeObj.dataName] = "";
        this.applyStyles();
        fontSizeElement.value = this.getDefaultFontSize(fontSizeObj.target);
      });

      fontSizeControls.appendChild(fontSizeElement);
      fontSizeControls.appendChild(resetFontBtn);
      fontSizeElementContainer.appendChild(fontSizeTitle);
      fontSizeElementContainer.appendChild(fontSizeControls);
    }

    const onShow = () => {
      if (fontSizeElement && fontSizeElement !== null) {
        fontSizeElement.value =
          this.data.styled[fontSizeObj.dataName] !== ""
            ? this.data.styled[fontSizeObj.dataName]
            : this.getDefaultFontSize(fontSizeObj.target);
      }
    };

    const popup = domUtils.Popup({ btnElement: element, onShow: onShow });

    const color = this.colorPicker(dataName);

    popup.appendChild(title);
    if (alignComponent && alignComponent !== null) {
      popup.appendChild(alignComponent);
    }
    if (slider && slider !== null) {
      popup.appendChild(slider);
    }
    if (fontSizeElementContainer && fontSizeElementContainer !== null) {
      popup.appendChild(fontSizeElementContainer);
    }
    if (fontWeightSelect && fontWeightSelect !== null) {
      popup.appendChild(fontWeightSelect);
    }
    popup.appendChild(color);
    return popup;
  };

  colorPicker = (dataName) => {
    const colorPickerMain = domUtils.make("div", ["picker-main"]);
    const colorSection = domUtils.make("div", [
      "picker-section",
      "color-section",
    ]);
    const colorSectionTitle = domUtils.make("label", [
      "picker-title",
      "picker-color-label",
    ]);
    const colorSectionSwatchGroup = domUtils.make("div", [
      "picker-swatch-group",
    ]);
    const opacitySection = domUtils.make("div", [
      "picker-section",
      "opacity-section",
    ]);
    const opacitySectionTitle = domUtils.make("label", [
      "picker-title",
      "picker-opacity-label",
    ]);
    const opacitySlider = domUtils.make("input", ["picker-slider"]);
    const resetBtn = domUtils.make("button", [
      "picker-button",
      "color-reset-button",
    ]);

    let colorValue = this.parseRGBA(this.data.styled[dataName]);
    const swatches = this.config.options.swatches;

    colorSectionTitle.innerText = "Colour";
    opacitySectionTitle.innerText = "Opacity";

    opacitySlider.type = "range";
    opacitySlider.min = 0;
    opacitySlider.max = 1;
    opacitySlider.step = 0.01;

    resetBtn.innerText = "Reset to default colour";

    const toRGBA = (hex, alpha = 1) => {
      hex = hex.replace(/^#/, "");

      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((c) => c + c)
          .join("");
      }

      if (hex.length !== 6) {
        throw new Error("Invalid HEX color.");
      }

      const bigint = parseInt(hex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;

      return [r, g, b, alpha];
    };

    const rgbEquals = (swatchColor) => {
      if(!colorValue) return false;
      const rgbSwatch = swatchColor === "#00000000" ? [255,255,255,0] : toRGBA(swatchColor, 1);
      const rgb1 = colorValue ? colorValue.slice(0, 3) : [];
      const rgb2 = rgbSwatch ? rgbSwatch.slice(0, 3) : [];
      if(rgb1.length > 0 && rgb2.length > 0) {
        return rgb1[0] === rgb2[0] && rgb1[1] === rgb2[1] && rgb1[2] === rgb2[2];
      } else {
        return false;
      }
    }

    const addSwatch = (swatchColor) => {
      const swatch = domUtils.make("button", ["picker-swatch"]);
      const activeSwatch = rgbEquals(swatchColor);
      if (activeSwatch) {
        swatch.classList.add("active");
      }
      swatch.style.backgroundColor = swatchColor;
      if (swatchColor === "#00000000") {
        swatch.classList.add("transparent");
      }
      swatch.addEventListener("click", () => {
        colorValue =
          swatchColor === "#00000000"
            ? toRGBA("#FFFFFF", 0)
            : toRGBA(swatchColor, 1);
        opacitySlider.value = 1;
        opacitySlider.disabled = false;
        this.data.styled[dataName] = `rgba(${colorValue.join(",")})`;
        this.applyStyles();
        colorSectionSwatchGroup.querySelectorAll(".picker-swatch").forEach((el) => {
          el.classList.remove("active");
        });
        swatch.classList.add("active");
      });
      colorSectionSwatchGroup.appendChild(swatch);
    };

    swatches.forEach((swatchColor) => {
      addSwatch(swatchColor);
    });

    const colorString = (color) =>
      color !== null && color !== "" ? `rgba(${color.join(",")})` : "";

    opacitySlider.value = colorValue ? colorValue[3] : 1;

    opacitySlider.addEventListener("input", (e) => {
      colorValue[3] = parseFloat(e.target.value);
      this.data.styled[dataName] = colorString(colorValue);
      this.applyStyles(dataName, colorString(colorValue));
    });

    resetBtn.addEventListener("click", () => {
      this.data.styled[dataName] = "";
      colorValue = "";
      this.applyStyles();
    });

    colorSection.appendChild(colorSectionTitle);
    colorSection.appendChild(colorSectionSwatchGroup);
    opacitySection.appendChild(opacitySectionTitle);
    opacitySection.appendChild(opacitySlider);

    colorPickerMain.appendChild(colorSection);
    colorPickerMain.appendChild(opacitySection);
    colorPickerMain.appendChild(resetBtn);
    return colorPickerMain;
  };

  createSelectOptions(element, optionsArr, dataName) {
    optionsArr.forEach((e, i) => {
      const option = domUtils.make("option");
      option.text = e;
      option.value = i;
      element.appendChild(option);
    });

    element.selectedIndex = this.data.styled[dataName];
    element.onchange = () => {
      this.data.styled[dataName] = element.selectedIndex;
      this.toolbar.setAttribute(dataName, element.selectedIndex);
      this.applyStyles();
    };
  }

  createFontSizeInput(dataName, target) {
    const fontSizeInput = domUtils.make("input", ["picker-input-number"]);
    fontSizeInput.type = "number";
    fontSizeInput.min = "1";
    fontSizeInput.max = "32";
    fontSizeInput.value =
      this.data.styled[dataName] !== ""
        ? this.data.styled[dataName]
        : this.getDefaultFontSize(target);
    fontSizeInput.addEventListener("input", (e) => {
      if (e.target.value === "") {
        this.data.styled[dataName] = "";
        this.applyStyles();
        fontSizeInput.value = this.getDefaultFontSize(target);
      } else {
        this.data.styled[dataName] = e.target.value;
        this.applyStyles();
      }
    });

    return fontSizeInput;
  }

  createRangeSlider(name, checkBoxData, sliderData, sliderMin, sliderMax) {
    const componentMain = domUtils.make("div", [
      "picker-slider-component",
      "picker-component",
    ]);
    const componentOptions = domUtils.make("div", [
      "picker-slider-options",
      "picker-sub-component",
    ]);
    const title = domUtils.make("label", ["picker-title"]);
    const checkBox = domUtils.make("input", ["picker-checkbox"]);
    const slider = domUtils.make("input", ["picker-slider"]);

    title.innerText = name;
    checkBox.type = "checkbox";
    slider.type = "range";
    slider.min = sliderMin;
    slider.max = sliderMax;

    checkBox.checked = this.data.styled[checkBoxData] || false;
    slider.disabled = this.data.styled[checkBoxData]
      ? !this.data.styled[checkBoxData]
      : true;
    slider.value = this.data.styled[sliderData] || sliderMin;

    checkBox.addEventListener("change", (e) => {
      slider.disabled = !e.target.checked;
      this.data.styled[checkBoxData] = e.target.checked;
      this.applyStyles();
    });

    slider.addEventListener("input", (e) => {
      this.data.styled[sliderData] = e.target.value;
      this.applyStyles();
    });

    componentOptions.appendChild(checkBox);
    componentOptions.appendChild(slider);
    componentMain.appendChild(title);
    componentMain.appendChild(componentOptions);

    return componentMain;
  }

  createTableControl(icon, tooltip, specificClass) {
    const css = this._CSS;
    const blockBtnStyles = [css.blockBtn, css.blockBtnSmall, css.xxlIcon];
    let elementStyle = [];
    if (specificClass) {
      if (Array.isArray(specificClass)) {
        elementStyle = [...specificClass, ...blockBtnStyles];
      } else {
        elementStyle = [specificClass, ...blockBtnStyles];
      }
    }
    const wrapper = domUtils.make("div", "table-control-wrapper");
    const element = domUtils.make("button", elementStyle);
    BtnTooltip(element, tooltip);
    element.innerHTML = icon;
    wrapper.appendChild(element);
    return wrapper;
  }

  createFontWeightSelect(target, id) {
    const fontWeightOptions = ["Bold", "Standard", "Light"];
    const selectComponent = domUtils.make("div", ["picker-component"]);
    const selectLabel = domUtils.make("label", ["picker-title"]);
    const selectTag = domUtils.make("select", ["picker-select"]);
    selectTag.id = id;

    selectLabel.innerText = "Font weight";
    this.createSelectOptions(selectTag, fontWeightOptions, target);

    selectComponent.appendChild(selectLabel);
    selectComponent.appendChild(selectTag);
    return selectComponent;
  }

  createAlignmentControls(horizontalDataName, verticalDataName) {
    const css = this._CSS;
    const alignmentComponent = domUtils.make("div", ["picker-component"]);
    const horizontalAlignOptions = domUtils.make("div", [
      css.horizontalAlignOptions,
      css.pickerButtonGroup,
    ]);
    const verticalAlignOptions = domUtils.make("div", [
      css.verticalAlignOptions,
      css.pickerButtonGroup,
    ]);
    const horizontalLabel = domUtils.make("label", ["picker-title"]);
    const verticalLabel = domUtils.make("label", ["picker-title"]);

    horizontalLabel.innerText = "Horizontal align";
    verticalLabel.innerText = "Vertical align";

    const horizontalAlignmentListener = (element, value) => {
      element.addEventListener("click", () => {
        this.data.styled[horizontalDataName] = value;
        this.applyStyles();
      });
    };

    const verticalAlignmentListener = (element, value) => {
      element.addEventListener("click", () => {
        this.data.styled[verticalDataName] = value;
        this.applyStyles();
      });
    };

    const hLeftAlign = this.createTableControl(
      HorizontalLeft,
      "Left",
      "horizontal-align-left"
    );
    const hCenterAlign = this.createTableControl(
      HorizontalCenter,
      "Center",
      "horizontal-align-center"
    );
    const hRightAlign = this.createTableControl(
      HorizontalRight,
      "Right",
      "horizontal-align-right"
    );

    const vTopAlign = this.createTableControl(
      VerticalTop,
      "Top",
      "vertical-align-top"
    );
    const vCenterAlign = this.createTableControl(
      VerticalCenter,
      "Middle",
      "vertical-align-middle"
    );
    const vBottomAlign = this.createTableControl(
      VerticalBottom,
      "Bottom",
      "vertical-align-bottom"
    );

    horizontalAlignmentListener(hLeftAlign, "start");
    horizontalAlignmentListener(hCenterAlign, "center");
    horizontalAlignmentListener(hRightAlign, "end");

    verticalAlignmentListener(vTopAlign, "start");
    verticalAlignmentListener(vCenterAlign, "center");
    verticalAlignmentListener(vBottomAlign, "end");

    horizontalAlignOptions.appendChild(hLeftAlign);
    horizontalAlignOptions.appendChild(hCenterAlign);
    horizontalAlignOptions.appendChild(hRightAlign);

    verticalAlignOptions.appendChild(vTopAlign);
    verticalAlignOptions.appendChild(vCenterAlign);
    verticalAlignOptions.appendChild(vBottomAlign);

    alignmentComponent.appendChild(horizontalLabel);
    alignmentComponent.appendChild(horizontalAlignOptions);
    alignmentComponent.appendChild(verticalLabel);
    alignmentComponent.appendChild(verticalAlignOptions);

    return alignmentComponent;
  }

  getDefaultFontSize(target) {
    if (this.table !== null) {
      switch (target) {
        case "firstRow":
          const firstRow = this.table.table.querySelector(".tc-row");
          const tcCells = firstRow.querySelectorAll(".tc-cell");
          const firstRowTargetCell =
            tcCells.length >= 2 ? tcCells[1] : tcCells[0];
          return parseFloat(
            window.getComputedStyle(firstRowTargetCell).fontSize
          );
        case "firstCol":
          const firstColCells = this.table.table.querySelectorAll(
            ".tc-cell:first-child"
          );
          const firstColTargetCell =
            firstColCells.length >= 2 ? firstColCells[1] : firstColCells[0];
          return parseFloat(
            window.getComputedStyle(firstColTargetCell).fontSize
          );
        default:
          const allRows = this.table.table.querySelectorAll(".tc-row");
          const targetRowCells =
            allRows[allRows.length >= 2 ? 1 : 0].querySelectorAll(".tc-cell");
          const defaultTargetCell =
            targetRowCells.length >= 2 ? targetRowCells[1] : targetRowCells[0];
          return parseFloat(
            window.getComputedStyle(defaultTargetCell).fontSize
          );
      }
    }
  }

  applyStyles(tempData, tempValue) {
    const allCells = this.container.querySelectorAll(".tc-cell");
    const allRows = this.container.querySelectorAll(".tc-row");
    const firstColumn = this.container.querySelectorAll(
      ".tc-row .tc-cell:first-child"
    );
    const firstRow = this.container.querySelectorAll(
      ".tc-row:first-child .tc-cell"
    );
    const table = this.container.querySelector(".tc-table");
    const processCells = (callback, borderReset) => {
      const rowCount = allRows.length;
      allRows.forEach((rowElement, rowIndex) => {
        const rowCells = rowElement.querySelectorAll(".tc-cell");
        const cellCount = rowCells.length;
        rowCells.forEach((cellElement, cellIndex) => {
          if (borderReset) {
            resetCell(cellElement);
          }
          callback({
            rowElement,
            rowIndex,
            cellElement,
            cellIndex,
            rowCount,
            cellCount,
          });
        });
      });
    };

    // Fonts
    const fontColor =
      tempData === "fontColor" ? tempValue : this.data.styled.fontColor;
    const firstRowFontColor =
      tempData === "firstRowFontColor"
        ? tempValue
        : this.data.styled.firstRowFontColor;
    const firstColFontColor =
      tempData === "firstColFontColor"
        ? tempValue
        : this.data.styled.firstColFontColor;

    allCells.forEach((e) => {
      e.style.color = fontColor;
      // Font size
      if (this.data.styled.fontSize !== "") {
        e.style.fontSize = `${this.data.styled.fontSize}px`;
      } else {
        e.style.fontSize = "";
      }
      // Font weight
      if (this.data.styled.fontWeight !== 1) {
        e.style.fontWeight = this.data.styled.fontWeight === 0 ? "700" : "300";
      } else {
        e.style.fontWeight = "";
      }
      // Cell content alignment
      e.style.textAlign = this.data.styled.horizontalAlign;
      e.style.justifyContent = this.data.styled.horizontalAlign;
      e.style.alignItems = this.data.styled.verticalAlign;
    });

    // Custom first row height

    if (
      this.data.styled.customFirstRowHeight &&
      this.data.styled.firstRowHeight > 0
    ) {
      table.style.gridTemplateRows = `${
        this.data.styled.firstRowHeight
      }% repeat(${allRows.length - 1}, 1fr)`;
    } else {
      table.style.gridTemplateRows = "";
    }

    allRows.forEach((e, i) => {
      if (
        this.data.styled.customFirstColWidth &&
        this.data.styled.firstColWidth > 5
      ) {
        e.style.gridTemplateColumns = `minmax(5%, ${this.data.styled.firstColWidth}%)`;
      } else {
        e.style.gridTemplateColumns = "";
      }
    });

    firstColumn.forEach((e) => {
      e.style.color = firstColFontColor || fontColor;
      // Font size
      if (this.data.styled.firstColFontSize !== "") {
        e.style.fontSize = `${this.data.styled.firstColFontSize}px`;
      } else {
        if (this.data.styled.fontSize !== "") {
          e.style.fontSize = `${this.data.styled.fontSize}px`;
        } else {
          e.style.fontSize = "";
        }
      }
      // Font weight
      if (this.data.styled.firstColFontWeight !== 1) {
        e.style.fontWeight =
          this.data.styled.firstColFontWeight === 0 ? "700" : "300";
      } else {
        if (this.data.styled.fontWeight !== 1) {
          e.style.fontWeight =
            this.data.styled.fontWeight === 0 ? "700" : "300";
        } else {
          e.style.fontWeight = "";
        }
      }

      // Cell content alignment
      e.style.textAlign = this.data.styled.firstColHorizontalAlign;
      e.style.justifyContent = this.data.styled.firstColHorizontalAlign;
      e.style.alignItems = this.data.styled.firstColVerticalAlign;
    });

    firstRow.forEach((e) => {
      e.style.color = firstRowFontColor || fontColor;
      // Font size
      if (this.data.styled.firstRowFontSize !== "") {
        e.style.fontSize = `${this.data.styled.firstRowFontSize}px`;
      } else {
        if (this.data.styled.fontSize !== "") {
          e.style.fontSize = `${this.data.styled.fontSize}px`;
        } else {
          e.style.fontSize = "";
        }
      }
      // Font weight
      if (this.data.styled.firstRowFontWeight !== 1) {
        e.style.fontWeight =
          this.data.styled.firstRowFontWeight === 0 ? "700" : "300";
      } else {
        if (this.data.styled.fontWeight !== 1) {
          e.style.fontWeight =
            this.data.styled.fontWeight === 0 ? "700" : "300";
        } else {
          e.style.fontWeight = "";
        }
      }

      // Cell content alignment
      e.style.textAlign = this.data.styled.firstRowHorizontalAlign;
      e.style.justifyContent = this.data.styled.firstRowHorizontalAlign;
      e.style.alignItems = this.data.styled.firstRowVerticalAlign;
    });

    // Background Colors

    const primaryBg =
      tempData === "backgroundColor"
        ? tempValue
        : this.data.styled.backgroundColor;
    const secondaryBg =
      tempData === "secondaryBGColor"
        ? tempValue
        : this.data.styled.secondaryBGColor;

    if (this.data.styled.tableStyle === 0) {
      // Default
      allCells.forEach((e) => {
        e.style.backgroundColor = primaryBg;
      });
    } else if (this.data.styled.tableStyle === 1) {
      // Alternating Rows
      const altRows = ({ rowIndex, cellElement }) => {
        if (rowIndex % 2 === 0) {
          cellElement.style.backgroundColor = primaryBg;
        } else {
          cellElement.style.backgroundColor = secondaryBg;
        }
      };
      processCells(altRows);
    } else if (this.data.styled.tableStyle === 2) {
      const altCols = ({ cellElement, cellIndex }) => {
        if (cellIndex % 2 === 0) {
          cellElement.style.backgroundColor = primaryBg;
        } else {
          cellElement.style.backgroundColor = secondaryBg;
        }
      };
      processCells(altCols);
    }

    if (
      this.data.styled.firstColBGColor !== "" ||
      tempData === "firstColBGColor"
    ) {
      firstColumn.forEach((e) => {
        e.style.backgroundColor =
          tempData === "firstColBGColor"
            ? tempValue
            : this.data.styled.firstColBGColor;
      });
    }

    if (
      this.data.styled.firstRowBGColor !== "" ||
      tempData === "firstRowBGColor"
    ) {
      firstRow.forEach((e) => {
        e.style.backgroundColor =
          tempData === "firstRowBGColor"
            ? tempValue
            : this.data.styled.firstRowBGColor;
      });
    }

    // Grid lines
    const gridStyle = `1px solid ${
      tempData === "gridColor" ? tempValue : this.data.styled.gridColor
    }`;
    const resetCell = (cellElement) => {
      cellElement.style.borderTop = "";
      cellElement.style.borderBottom = "";
      cellElement.style.borderLeft = "";
      cellElement.style.borderRight = "";
    };

    if (this.data.styled.gridType === 0) {
      processCells(() => null, true);
    } else if (this.data.styled.gridType === 1) {
      // All borders
      const allBorder = ({
        rowIndex,
        cellElement,
        cellIndex,
        rowCount,
        cellCount,
      }) => {
        if (cellIndex === cellCount - 1) {
          cellElement.style.borderRight = gridStyle;
        }
        if (rowIndex === rowCount - 1) {
          cellElement.style.borderBottom = gridStyle;
        }
        cellElement.style.borderTop = gridStyle;
        cellElement.style.borderLeft = gridStyle;
      };
      processCells(allBorder, true);
    } else if (this.data.styled.gridType === 2) {
      // Horizontal borders
      const horizontalBorder = ({ rowIndex, cellElement }) => {
        if (rowIndex === 0) {
          cellElement.style.borderTop = gridStyle;
          cellElement.style.borderBottom = gridStyle;
        } else {
          cellElement.style.borderBottom = gridStyle;
        }
      };
      processCells(horizontalBorder, true);
    } else if (this.data.styled.gridType === 3) {
      const verticalBorder = ({ cellElement, cellIndex }) => {
        if (cellIndex === 0) {
          cellElement.style.borderLeft = gridStyle;
          cellElement.style.borderRight = gridStyle;
        } else {
          cellElement.style.borderRight = gridStyle;
        }
      };
      processCells(verticalBorder, true);
    }
  }

  renderColumnControls() {
    const css = this._CSS;
    const columnControlsGroup = domUtils.make("div", [
      css.columnControlsGroup,
      css.stylesGroup,
    ]);
    const columnControlsGroupLabel = domUtils.make("label", [
      css.tableStyleLabel,
      css.editorBlockModalLabel,
      css.adminFont,
      css.columnControlsGroupLabel,
    ]);
    const columnControlsOptions = domUtils.make("div", [
      css.columnControlsOptions,
      css.controlsOptions,
    ]);
    const columnAddRight = this.createTableControl(
      AddColRight,
      "Add column right",
      css.columnAddRight
    );
    const columnAddLeft = this.createTableControl(
      AddColLeft,
      "Add column left",
      css.columnAddLeft
    );
    const columnDelete = this.createTableControl(
      DeleteCol,
      "Delete column",
      css.columnDelete
    );

    columnControlsGroupLabel.innerText = "Columns";

    columnAddRight.addEventListener("click", () =>
      this.table.addColumnToRight()
    );
    columnAddLeft.addEventListener("click", () => this.table.addColumnToLeft());
    columnDelete.addEventListener("click", () =>
      this.table.deleteFocusedColumn()
    );

    columnControlsOptions.appendChild(columnAddRight);
    columnControlsOptions.appendChild(columnAddLeft);
    columnControlsOptions.appendChild(columnDelete);
    columnControlsGroup.appendChild(columnControlsGroupLabel);
    columnControlsGroup.appendChild(columnControlsOptions);

    return columnControlsGroup;
  }

  renderRowControls() {
    const css = this._CSS;
    const rowControlsGroup = domUtils.make("div", [
      css.rowControlsGroup,
      css.stylesGroup,
    ]);
    const rowControlsGroupLabel = domUtils.make("label", [
      css.tableStyleLabel,
      css.editorBlockModalLabel,
      css.adminFont,
      css.rowControlsGroupLabel,
    ]);
    const rowControlsOptions = domUtils.make("div", [
      css.rowControlsOptions,
      css.controlsOptions,
    ]);
    const rowAddAbove = this.createTableControl(
      AddRowAbove,
      "Add row above",
      css.rowAddAbove
    );
    const rowAddBelow = this.createTableControl(
      AddRowBelow,
      "Add row below",
      css.rowAddBelow
    );
    const rowDelete = this.createTableControl(
      DeleteRow,
      "Delete row",
      css.rowDelete
    );

    rowControlsGroupLabel.innerText = "Rows";

    rowAddAbove.addEventListener("click", () => this.table.addRowAbove());
    rowAddBelow.addEventListener("click", () => this.table.addRowBelow());
    rowDelete.addEventListener("click", () => this.table.deleteFocusedRow());

    rowControlsOptions.appendChild(rowAddAbove);
    rowControlsOptions.appendChild(rowAddBelow);
    rowControlsOptions.appendChild(rowDelete);
    rowControlsGroup.appendChild(rowControlsGroupLabel);
    rowControlsGroup.appendChild(rowControlsOptions);

    return rowControlsGroup;
  }

  renderTableStyles() {
    const css = this._CSS;
    const editorLabelStyles = [
      css.tableStyleLabel,
      css.editorBlockModalLabel,
      css.adminFont,
    ];
    const makeLabel = (classname) =>
      domUtils.make("label", [classname, ...editorLabelStyles]);

    const tableStylesGroup = domUtils.make("div", [
      css.tableStylesGroup,
      css.stylesGroup,
    ]);
    const tableStylesGroupLabel = makeLabel(css.tableStylesGroupLabel);

    const tableStyles = ["Default", "Alternating Rows", "Alternating Columns"];

    const tableStyleCellsOptions = domUtils.make("div", [
      css.tableStyleCellsOptions,
      css.groupOptions,
    ]);

    const firstRowHeightSlider = this.createRangeSlider(
      "Height",
      "customFirstRowHeight",
      "firstRowHeight",
      0,
      50
    );
    const firstColWidthSlider = this.createRangeSlider(
      "Width",
      "customFirstColWidth",
      "firstColWidth",
      5,
      30
    );

    this.tableStyleInput = domUtils.make("select", [css.tableStyleInput]);
    const backgroundColorInput = this.createTableControl(
      DefaultTableBgIcon + AltRowPrimary + AltColPrimary,
      "Primary background colour",
      css.backgroundColorInput
    );
    const secondaryBGColorInput = this.createTableControl(
      AltRowSecondary + AltColSecondary,
      "Secondary background colour",
      [css.backgroundColorInput, css.backgroundColorInputSecondary]
    );
    const firstRowBGInput = this.createTableControl(
      FirstRowIcon,
      "First row settings",
      css.firstRowBGInput
    );
    const firstColBGInput = this.createTableControl(
      FirstColumnIcon,
      "First column settings",
      css.firstColBGInput
    );

    tableStylesGroupLabel.innerText = "Table style";

    this.createSelectOptions(this.tableStyleInput, tableStyles, "tableStyle");

    tableStyleCellsOptions.appendChild(this.tableStyleInput);
    tableStyleCellsOptions.appendChild(backgroundColorInput);
    tableStyleCellsOptions.appendChild(secondaryBGColorInput);
    tableStyleCellsOptions.appendChild(firstRowBGInput);
    tableStyleCellsOptions.appendChild(firstColBGInput);
    tableStylesGroup.appendChild(tableStylesGroupLabel);
    tableStylesGroup.appendChild(tableStyleCellsOptions);

    this.createPopup({
      element: backgroundColorInput,
      dataName: "backgroundColor",
      pickerName: "Primary background",
    });
    this.createPopup({
      element: secondaryBGColorInput,
      dataName: "secondaryBGColor",
      pickerName: "Secondary background",
    });
    this.createPopup({
      element: firstRowBGInput,
      dataName: "firstRowBGColor",
      pickerName: "First row",
      slider: firstRowHeightSlider,
    });
    this.createPopup({
      element: firstColBGInput,
      dataName: "firstColBGColor",
      pickerName: "First column",
      slider: firstColWidthSlider,
    });

    return tableStylesGroup;
  }

  renderGridStyles() {
    const css = this._CSS;
    const editorLabelStyles = [
      css.tableStyleLabel,
      css.editorBlockModalLabel,
      css.adminFont,
    ];
    const makeLabel = (classname) =>
      domUtils.make("label", [classname, ...editorLabelStyles]);

    const gridStylesGroup = domUtils.make("div", [
      css.gridStylesGroup,
      css.stylesGroup,
    ]);
    const gridStyleOptions = domUtils.make("div", [css.groupOptions]);

    const gridTypes = ["None", "All", "Horizontal", "Vertical"];

    const gridTypeLabel = makeLabel(css.gridTypeLabel);
    this.gridTypeInput = domUtils.make("select", [css.gridTypeInput]);
    const gridColorInput = this.createTableControl(
      GridIcon,
      "Grid line colour",
      css.gridColorInput
    );

    gridTypeLabel.innerText = "Grid";

    this.createSelectOptions(this.gridTypeInput, gridTypes, "gridType");

    gridStyleOptions.appendChild(this.gridTypeInput);
    gridStyleOptions.appendChild(gridColorInput);
    gridStylesGroup.appendChild(gridTypeLabel);
    gridStylesGroup.appendChild(gridStyleOptions);

    this.createPopup({
      element: gridColorInput,
      dataName: "gridColor",
      pickerName: "Grid line",
    });

    return gridStylesGroup;
  }

  renderFontStyles() {
    const css = this._CSS;
    const editorLabelStyles = [
      css.tableStyleLabel,
      css.editorBlockModalLabel,
      css.adminFont,
    ];
    const makeLabel = (classname) =>
      domUtils.make("label", [classname, ...editorLabelStyles]);

    const fontStyleGroup = domUtils.make("div", [
      css.fontStyleGroup,
      css.stylesGroup,
    ]);
    const fontStyleGroupLabel = makeLabel(css.fontStyleGroupLabel);
    const fontStyleOptions = domUtils.make("div", [
      css.fontStyleGroup,
      css.fontStyleOptions,
      css.controlsOptions,
    ]);

    fontStyleGroupLabel.innerText = "Font";
    const fontSizeMainInput = this.createFontSizeInput("fontSize", "");
    const firstRowFontSizeInput = this.createFontSizeInput(
      "firstRowFontSize",
      "firstRow"
    );
    const firstColumnFontSizeInput = this.createFontSizeInput(
      "firstColFontSize",
      "firstCol"
    );

    const fontWeightMainInput = this.createFontWeightSelect(
      "fontWeight",
      "font-main-input-select"
    );
    const firstRowFontWeightInput = this.createFontWeightSelect(
      "firstRowFontWeight",
      "font-first-row-input-select"
    );
    const firstColumnFontWeightInput = this.createFontWeightSelect(
      "firstColFontWeight",
      "font-first-col-input-select"
    );

    const fontColorInput = this.createTableControl(
      DefaultTableBgIcon,
      "Default font",
      css.firstRowBGInput
    );
    const firstRowFontColorInput = this.createTableControl(
      FirstRowIcon,
      "First row font",
      css.firstRowBGInput
    );
    const firstColFontColorInput = this.createTableControl(
      FirstColumnIcon,
      "First column font",
      css.firstColBGInput
    );

    const mainAlignComponent = this.createAlignmentControls(
      "horizontalAlign",
      "verticalAlign"
    );
    const firstRowAlignComponent = this.createAlignmentControls(
      "firstRowHorizontalAlign",
      "firstRowVerticalAlign"
    );
    const firstColAlignComponent = this.createAlignmentControls(
      "firstColHorizontalAlign",
      "firstColVerticalAlign"
    );

    fontStyleOptions.appendChild(fontColorInput);
    fontStyleOptions.appendChild(firstRowFontColorInput);
    fontStyleOptions.appendChild(firstColFontColorInput);
    fontStyleGroup.appendChild(fontStyleGroupLabel);
    fontStyleGroup.appendChild(fontStyleOptions);

    const fontSizeObj = {
      name: "Default font size",
      element: fontSizeMainInput,
      dataName: "fontSize",
      target: "",
    };
    const firstRowFontSizeObj = {
      name: "First row font size",
      element: firstRowFontSizeInput,
      dataName: "firstRowFontSize",
      target: "firstRow",
    };
    const firstColFontSizeObj = {
      name: "First column font size",
      element: firstColumnFontSizeInput,
      dataName: "firstColFontSize",
      target: "firstCol",
    };

    this.createPopup({
      element: fontColorInput,
      dataName: "fontColor",
      pickerName: "Default font",
      fontSizeObj: fontSizeObj,
      fontWeightSelect: fontWeightMainInput,
      alignComponent: mainAlignComponent,
    });
    this.createPopup({
      element: firstRowFontColorInput,
      dataName: "firstRowFontColor",
      pickerName: "First row font",
      fontSizeObj: firstRowFontSizeObj,
      fontWeightSelect: firstRowFontWeightInput,
      alignComponent: firstRowAlignComponent,
    });
    this.createPopup({
      element: firstColFontColorInput,
      dataName: "firstColFontColor",
      pickerName: "First column font",
      fontSizeObj: firstColFontSizeObj,
      fontWeightSelect: firstColumnFontWeightInput,
      alignComponent: firstColAlignComponent,
    });

    return fontStyleGroup;
  }

  renderResetButton() {
    const css = this._CSS;
    const resetStylesGroup = domUtils.make("div", css.resetStylesGroup);
    const resetBtn = domUtils.make("button", [
      css.resetStylesButton,
      css.blockBtn,
      css.cancel,
    ]);
    const modalMain = domUtils.make("div", [css.confirmResetModalMain]);
    const modalBackdrop = domUtils.make("div", [
      css.confirmResetModalBackdrop,
      css.editorBlockModalBackdrop,
    ]);
    const modal = domUtils.make("div", [
      css.confirmResetModal,
      css.editorBlockModal,
    ]);
    const modalTitle = domUtils.make("h6", [css.editorBlockModalTitle]);
    const modalOptions = domUtils.make("div", [css.confirmModalOptions]);
    const confirm = domUtils.make("button", [
      css.confirmResetBtn,
      css.blockBtn,
      css.save,
    ]);
    const cancel = domUtils.make("button", [
      css.cancelResetBtn,
      css.blockBtn,
      css.cancel,
    ]);

    resetBtn.innerHTML = `Reset ${ResetIcon}`;
    modalTitle.innerText = "Are you sure you want to clear styles?";
    confirm.innerText = "Confirm";
    cancel.innerText = "Cancel";

    resetBtn.addEventListener("click", () => {
      if (!modalMain.classList.contains("show")) {
        modalMain.classList.add("show");
      }
    });

    confirm.addEventListener("click", () => {
      this.toolbar.remove();

      const styled = {
        tableStyle: 0,
        customFirstRowHeight: false,
        firstRowHeight: "",
        customFirstColWidth: false,
        firstColWidth: "",
        gridType: 0,
        gridColor: "rgba(0, 0, 0, 1)",
        fontColor: "",
        fontSize: "",
        fontWeight: 1,
        backgroundColor: "",
        secondaryBGColor: "",
        firstRowFontSize: "",
        firstRowFontWeight: 1,
        firstRowFontColor: "",
        firstRowBGColor: "",
        firstColFontSize: "",
        firstColFontWeight: 1,
        firstColFontColor: "",
        firstColBGColor: "",
        horizontalAlign: "start",
        verticalAlign: "start",
        firstRowHorizontalAlign: "start",
        firstRowVerticalAlign: "start",
        firstColHorizontalAlign: "start",
        firstColVerticalAlign: "start",
      };
      this.data.styled = { ...styled };
      this.gridTypeInput.value = 0;
      this.tableStyleInput.value = 0;
      this.applyStyles();
      this.toolbar = this.renderStyleToolbar();
      this.toolbar.setAttribute("tableStyle", this.data.styled.tableStyle);
      this.toolbar.setAttribute("gridType", this.data.styled.gridType);
      this.container.appendChild(this.toolbar);
      modalMain.classList.remove("show");
    });

    cancel.addEventListener("click", () => {
      modalMain.classList.remove("show");
    });

    modalOptions.appendChild(confirm);
    modalOptions.appendChild(cancel);
    modal.appendChild(modalTitle);
    modal.appendChild(modalOptions);
    modalMain.appendChild(modalBackdrop);
    modalMain.appendChild(modal);
    resetStylesGroup.appendChild(resetBtn);
    resetStylesGroup.appendChild(modalMain);

    return resetStylesGroup;
  }

  renderStyleToolbar() {
    const css = this._CSS;
    const styleContainer = domUtils.make("div", [
      css.tableStylesContainer,
      css.editorBlockToolbar,
    ]);

    styleContainer.appendChild(this.renderColumnControls());
    styleContainer.appendChild(this.renderRowControls());
    if (this.config.draggable) {
      styleContainer.appendChild(this.renderTableStyles());
      styleContainer.appendChild(this.renderGridStyles());
      styleContainer.appendChild(this.renderFontStyles());
      styleContainer.appendChild(this.renderResetButton());
    }

    return styleContainer;
  }

  /**
   * Return Tool's view
   *
   * @returns {HTMLDivElement}
   */
  render() {
    /** creating table */
    this.table = new Table(this.readOnly, this.api, this.data, this.config);
    const editBtnContainer = domUtils.make("div");
    this.editBtn = domUtils.make("button", [
      this._CSS.blockBtn,
      this._CSS.xxlIcon,
      this._CSS.blockBtnSmall,
      this._CSS.editTableStyleBtn,
      this._CSS.focusVisibleBtn,
    ]);
    this.editBtn.innerHTML = EditIcon;

    /** creating container around table */
    this.container = domUtils.make("div", this.api.styles.block);
    this.container.appendChild(this.table.getWrapper());
    this.toolbar = this.renderStyleToolbar();
    this.table.setHeadingsSetting(this.data.withHeadings);
    this.csvFileInput = this.container.querySelector(".csv-file-input");

    this.toolbar.setAttribute("tableStyle", this.data.styled.tableStyle);
    this.toolbar.setAttribute("gridType", this.data.styled.gridType);

    BtnTooltip(this.editBtn, "Edit table styles");

    this.editBtn.addEventListener("click", () => {
      if (this.toolbar.classList.contains("show")) {
        this.toolbar.classList.remove("show");
        this.editBtn.classList.remove(this._CSS.clear);
      } else {
        this.toolbar.classList.add("show");
        this.editBtn.classList.add(this._CSS.clear);
      }
    });

    if (!this.readOnly) {
      this.csvFileInput.addEventListener("change", (event) => {
        this.csvFileHandler(event);
      });
      editBtnContainer.appendChild(this.editBtn);
    }
    this.container.appendChild(editBtnContainer);
    this.container.appendChild(this.toolbar);

    return this.container;
  }

  rendered() {
    if (this.config.draggable) {
      this.applyStyles();
      this.table.applyStyles = () => this.applyStyles();
    }

    this.mainContainer = this.container.closest(".ce-block");
    this.mainContainer.addEventListener("focusout", (event) => {
      setTimeout(() => {
        if (!this.mainContainer.contains(document.activeElement)) {
          this.hideToolbar();
        }
      }, 10);
    });
  }

  hideToolbar() {
    if (this.toolbar.classList.contains("show")) {
      this.toolbar.classList.remove("show");
      this.editBtn.classList.remove(this._CSS.clear);
      this.toolbar.querySelectorAll("table-input-popup").forEach((popup) => {
        popup.classList.remove("table-popup--show");
        popup.classList.remove("table-popup--active");
      });
    }
  }

  /**
   * Add plugin settings
   *
   * @returns {HTMLElement} - wrapper element
   */
  renderSettings() {
    const wrapper = domUtils.make("div", "cdx-table-settings");
    // const tunes = [
    //   {
    //     name: this.api.i18n.t('With headings'),
    //     icon: IconTableWithHeadings,
    //     isActive: this.data.withHeadings,
    //     setTune: () => {
    //       this.data.withHeadings = true;
    //     }
    //   },
    //   {
    //     name: this.api.i18n.t('Without headings'),
    //     icon: IconTableWithoutHeadings,
    //     isActive: !this.data.withHeadings,
    //     setTune: () => {
    //       this.data.withHeadings = false;
    //     }
    //   },
    // ];

    // tunes.map(tune => {
    //   const button = document.createElement('button');
    //   button.classList.add(this.api.styles.settingsButton);
    //   button.innerHTML = tune.icon;

    //   if (tune.isActive) {
    //     button.classList.add(this.api.styles.settingsButtonActive);
    //   } else {
    //     button.classList.remove(this.api.styles.settingsButtonActive);
    //   }

    //   button.addEventListener('click', () => this.toggleTune(tune, button));

    //   this.api.tooltip.onHover(button, tune.name, {
    //     placement: 'top',
    //     hidingDelay: 500
    //   });

    //   wrapper.appendChild(button);
    //   return button
    // });
    return wrapper;
  }

  /**
   * Extract table data from the view
   *
   * @returns {TableData} - saved data
   */
  save() {
    const tableContent = this.table.getData();
    let result = {
      withHeadings: this.data.withHeadings,
      content: tableContent,
      styled: { ...this.data.styled },
    };

    return result;
  }

  /**
   * Changes the state of the tune
   * Updates its representation in the table
   *
   * @param {Tune} tune - one of the table settings
   * @param {HTMLElement} tuneButton - DOM element of the tune
   * @returns {void}
   */
  toggleTune(tune, tuneButton) {
    const buttons = tuneButton.parentNode.querySelectorAll(
      "." + this.api.styles.settingsButton
    );

    // Clear other buttons
    Array.from(buttons).forEach((button) =>
      button.classList.remove(this.api.styles.settingsButtonActive)
    );

    // Mark active button
    tuneButton.classList.toggle(this.api.styles.settingsButtonActive);
    tune.setTune();

    this.table.setHeadingsSetting(this.data.withHeadings);
  }


  static get sanitize() {
    return {
      span: true,
      b: true,
      a: true,
      sub: true,
      sup: true,
    };
  }
  /**
   * Plugin destroyer
   *
   * @returns {void}
   */
  destroy() {
    this.table.destroy();
  }
}
